import {
  DatePicker,
  DayOfWeek,
  FirstWeekOfYear,
  IDatePickerStrings,
  IDatePickerStyleProps,
  IDatePickerStyles,
  IStyleFunctionOrObject,
} from '@fluentui/react';
import { IPIconButton, IPTooltipHost } from 'baseComponents';
import { IPStack } from 'baseComponents/Stack/IPStack';
import { cancelIcon } from 'globalStyles';
import { useTranslation } from 'react-i18next';
import { toLocaleDateLong, toLocaleDateNumeric, toLocaleDateShort } from 'utils/datetime';

interface ILocalizedDatePickerProps {
  value?: Date;
  onDateChange: (value: Date | undefined) => void;
  showWeekNumbers?: boolean;
  showMonthPickerAsOverlay?: boolean;
  minDate?: Date;
  maxDate?: Date;
  label?: string;
  showGoToToday?: boolean;
  styles?: IStyleFunctionOrObject<IDatePickerStyleProps, IDatePickerStyles>;
  disabled?: boolean;
  underline?: boolean;
  format?: 'long' | 'short' | 'numeric';
  allowEmptyValue?: boolean;
  placeholder?: string;
}

const LocalizedDatePicker = (props: ILocalizedDatePickerProps) => {
  const { t } = useTranslation(['dateTimeComponent']);

  const dateChangeHandler = (date: Date | null | undefined) => {
    if (date) {
      props.onDateChange(date);
    }
  };

  const DayPickerStrings: IDatePickerStrings = {
    months: [
      t('dateTimeComponent:Months.January'),
      t('dateTimeComponent:Months.February'),
      t('dateTimeComponent:Months.March'),
      t('dateTimeComponent:Months.April'),
      t('dateTimeComponent:Months.May'),
      t('dateTimeComponent:Months.June'),
      t('dateTimeComponent:Months.July'),
      t('dateTimeComponent:Months.August'),
      t('dateTimeComponent:Months.September'),
      t('dateTimeComponent:Months.October'),
      t('dateTimeComponent:Months.November'),
      t('dateTimeComponent:Months.December'),
    ],

    shortMonths: [
      t('dateTimeComponent:MonthsShort.Jan'),
      t('dateTimeComponent:MonthsShort.Feb'),
      t('dateTimeComponent:MonthsShort.Mar'),
      t('dateTimeComponent:MonthsShort.Apr'),
      t('dateTimeComponent:MonthsShort.May'),
      t('dateTimeComponent:MonthsShort.Jun'),
      t('dateTimeComponent:MonthsShort.Jul'),
      t('dateTimeComponent:MonthsShort.Aug'),
      t('dateTimeComponent:MonthsShort.Sep'),
      t('dateTimeComponent:MonthsShort.Oct'),
      t('dateTimeComponent:MonthsShort.Nov'),
      t('dateTimeComponent:MonthsShort.Dec'),
    ],

    days: [
      t('dateTimeComponent:WeekDays.Sunday'),
      t('dateTimeComponent:WeekDays.Monday'),
      t('dateTimeComponent:WeekDays.Tuesday'),
      t('dateTimeComponent:WeekDays.Wednesday'),
      t('dateTimeComponent:WeekDays.Thursday'),
      t('dateTimeComponent:WeekDays.Friday'),
      t('dateTimeComponent:WeekDays.Saturday'),
    ],

    shortDays: [
      t('dateTimeComponent:WeekDays.Sunday')[0],
      t('dateTimeComponent:WeekDays.Monday')[0],
      t('dateTimeComponent:WeekDays.Tuesday')[0],
      t('dateTimeComponent:WeekDays.Wednesday')[0],
      t('dateTimeComponent:WeekDays.Thursday')[0],
      t('dateTimeComponent:WeekDays.Friday')[0],
      t('dateTimeComponent:WeekDays.Saturday')[0],
    ],

    goToToday: t('dateTimeComponent:Extra.GoToToday'),
    prevMonthAriaLabel: t('dateTimeComponent:Extra.GoToPreviousMonth'),
    nextMonthAriaLabel: t('dateTimeComponent:Extra.GoToNextMonth'),
    prevYearAriaLabel: t('dateTimeComponent:Extra.GoToPreviousYear'),
    nextYearAriaLabel: t('dateTimeComponent:Extra.GoToNextYear'),

    isRequiredErrorMessage: t('dateTimeComponent:Errors.isRequiredErrorMessage'),
    invalidInputErrorMessage: t('dateTimeComponent:Errors.invalidInputErrorMessage'),
    isOutOfBoundsErrorMessage: t('dateTimeComponent:Errors.isOutOfBoundsErrorMessage'),
    isResetStatusMessage: t('dateTimeComponent:Errors.isResetStatusMessage'),
  };

  const onFormatDate = (date?: Date): string => {
    switch (props.format) {
      case 'long':
        return toLocaleDateLong(date);
      case 'short':
        return toLocaleDateShort(date);
      case 'numeric':
        return toLocaleDateNumeric(date);
      default:
        return toLocaleDateLong(date);
    }
  };

  const borderStyle = '1px solid grey';

  return (
    <IPStack horizontal verticalAlign="end">
      <IPStack.Item grow>
        <DatePicker
          disabled={props.disabled}
          styles={props.styles}
          showWeekNumbers={props.showWeekNumbers}
          firstWeekOfYear={FirstWeekOfYear.FirstFullWeek}
          showMonthPickerAsOverlay={props.showMonthPickerAsOverlay}
          firstDayOfWeek={DayOfWeek.Monday}
          value={props.value}
          strings={DayPickerStrings}
          onSelectDate={(date: Date | null | undefined) => dateChangeHandler(date)}
          minDate={props.minDate}
          maxDate={props.maxDate}
          highlightSelectedMonth={true}
          highlightCurrentMonth={true}
          formatDate={onFormatDate}
          label={props.label}
          placeholder={props.placeholder}
          showGoToToday={props.showGoToToday ?? true}
          underlined={props.underline}
        />
      </IPStack.Item>
      {props.allowEmptyValue && (
        <IPStack.Item>
          <IPTooltipHost content={t('dateTimeComponent:Extra.ClearDate')}>
            <IPIconButton
              disabled={!props.value}
              onClick={() => {
                props.onDateChange(undefined);
              }}
              iconProps={cancelIcon}
              styles={{
                root: { top: -5, borderRight: borderStyle, borderTop: borderStyle, borderBottom: borderStyle },
              }}
            />
          </IPTooltipHost>
        </IPStack.Item>
      )}
    </IPStack>
  );
};

export default LocalizedDatePicker;
